import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { learningStyleContent } from '../data/learningStyleContent';
import '../styles/QuizResults.css';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { personalityTypes } from '../data/personalityTypes';
import { useTheme, useMediaQuery } from '@mui/material';

ChartJS.register(ArcElement, Tooltip, Legend);

const QuizResultsPage = () => {
    const { resultId } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [data, setData] = useState(null);
    const [expandedStyle, setExpandedStyle] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchResults = async () => {
            if (!resultId) {
                setError('No result ID provided');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await axios.get(`api/quiz/results/${resultId}`);
                
                if (!response.data) {
                    throw new Error('No data received from server');
                }

                const result = response.data.result || response.data;
                
                if (!result) {
                    throw new Error('Invalid result structure');
                }

                const primaryStyle = result.primary_style?.toLowerCase() || 'visual';
                const enrichedResult = {
                    ...result,
                    learning_style_content: learningStyleContent[primaryStyle]
                };

                setData(enrichedResult);
            } catch (err) {
                console.error('Error fetching results:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchResults();
    }, [resultId]);

    const renderHeader = () => {
        if (!data) return null;

        const primary_style = data.primary_style
            ? data.primary_style.charAt(0).toUpperCase() + data.primary_style.slice(1)
            : 'Visual';
        
        const personality_type = data.personality_type || 'ENFP';

        const letterInfo = {
            'I': { word: 'NTROVERTED' },
            'N': { word: 'TUITIVE', prefix: 'I' },
            'F': { word: 'EELING' },
            'P': { word: 'ERCEIVING' },
            'E': { word: 'XTROVERTED' },
            'S': { word: 'ENSING' },
            'T': { word: 'HINKING' },
            'J': { word: 'UDGING' }
        };

        return (
            <div className="quiz-header" style={{
                padding: isMobile ? '1rem' : '2rem',
                textAlign: 'center'
            }}>
                <h1 className="results-title" style={{
                    fontSize: isMobile ? '1.5rem' : '2rem',
                    margin: isMobile ? '0.5rem 0' : '1rem 0'
                }}>
                    You are a{' '}
                    <span className={`highlight-text ${data.primary_style.toLowerCase()}`}>
                        {primary_style}-{personality_type}
                    </span>{' '}
                    learner
                </h1>
                <div className="personality-type-display" style={{
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '0.5rem' : '1rem',
                    fontSize: isMobile ? '0.9rem' : '1rem'
                }}>
                    {personality_type.split('').map((letter, index) => (
                        <span key={index} className="personality-letter-group">
                            {letterInfo[letter]?.prefix && <span>{letterInfo[letter].prefix}</span>}
                            <span className="letter-highlight">{letter}</span>
                            {letterInfo[letter]?.word || ''}
                            {index < personality_type.length - 1 && ' '}
                        </span>
                    ))}
                </div>
                <div className="decorative-line"></div>
            </div>
        );
    };

    const renderDetailedBreakdown = () => {
        if (!data?.personality_scores) return null;
        
        const scores = data.personality_scores;
        
        // Color key definitions
        const colorKeys = [
            { label: 'Introverted', color: '#4A90E2' },
            { label: 'Extroverted', color: '#F5A623' },
            { label: 'iNtuitive', color: '#F8E71C' },
            { label: 'Sensing', color: '#7ED321' },
            { label: 'Thinking', color: '#BD10E0' },
            { label: 'Feeling', color: '#50E3C2' },
            { label: 'Perceiving', color: '#D0021B' },
            { label: 'Judging', color: '#4A90E2' }
        ];

        return (
            <div className="detailed-breakdown" style={{
                padding: isMobile ? '1rem' : '2rem'
            }}>
                <h3 style={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                    Detailed breakdown of {data.personality_type} score:
                </h3>
                <p className="breakdown-description">
                    Just like most things in life, the results for you are not black and white. We all vary in the degree we adhere to each personality trait. Sometimes 
                    an introvert is almost an extrovert. Sometimes somebody who's thinking is also very feeling. These graphs break down in more detail your 
                    results. Just click on each color to see the exact score.
                </p>

                {/* Color key legend */}
                <div className="color-key-legend" style={{
                    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)',
                    gap: isMobile ? '0.5rem' : '1rem',
                    fontSize: isMobile ? '0.8rem' : '1rem'
                }}>
                    {colorKeys.map((key, index) => (
                        <div key={index} className="legend-item" style={{ backgroundColor: key.color }}>
                            {key.label}
                        </div>
                    ))}
                </div>

                <div className="score-bars" style={{
                    margin: isMobile ? '1rem 0' : '2rem 0'
                }}>
                    {[
                        { label: 'Introverted vs. Extroverted', score: scores.ie, left: 'introverted', right: 'extroverted' },
                        { label: 'Intuitive vs. Sensing', score: scores.sn, left: 'intuitive', right: 'sensing' },
                        { label: 'Thinking vs. Feeling', score: scores.tf, left: 'thinking', right: 'feeling' },
                        { label: 'Perceiving vs. Judging', score: scores.jp, left: 'perceiving', right: 'judging' }
                    ].map(({ label, score, left, right }) => (
                        <div key={label} className="score-bar-row">
                            <span className="label">{label}</span>
                            <div className="bar-container">
                                <div className={`bar ${left}`} style={{width: `${100 - score}%`}} />
                                <div className={`bar ${right}`} style={{width: `${score}%`}} />
                            </div>
                            {/* Add percentage tooltip */}
                            <div className="score-tooltip">
                                {label === 'Thinking vs. Feeling' && (
                                    <div className="tooltip-content">
                                        Thinking: {score.toFixed(2)}%
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const renderPersonalityDetails = () => {
        if (!data?.personality_type) return null;
        
        const typeInfo = personalityTypes[data.personality_type];
        
        if (!typeInfo) return null;

        return (
            <div className="personality-details" style={{
                padding: isMobile ? '1rem' : '2rem'
            }}>
                <h3 style={{ 
                    fontSize: isMobile ? '1.2rem' : '1.5rem',
                    marginBottom: '1.5rem'
                }}>
                    How {data.personality_type}s learn:
                </h3>

                <div className="info-text" style={{
                    backgroundColor: '#f5f5f5',
                    padding: '1.5rem',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                }}>
                    <p style={{
                        fontSize: isMobile ? '0.9rem' : '1rem',
                        lineHeight: '1.6',
                        margin: 0
                    }}>
                        {typeInfo.description}
                    </p>
                </div>

                <div className="learning-preferences" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.5rem',
                    width: '100%'
                }}>
                    <div className="optimal-situations" style={{
                        backgroundColor: '#f5f5f5',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        borderLeft: '4px solid #4CAF50'
                    }}>
                        <div className="icon">👍</div>
                        <h4 style={{ 
                            marginBottom: '1rem',
                            fontSize: isMobile ? '1rem' : '1.2rem' 
                        }}>
                            OPTIMAL LEARNING SITUATIONS:
                        </h4>
                        <ul style={{ 
                            paddingLeft: '1.5rem',
                            margin: 0 
                        }}>
                            {typeInfo.optimal_situations.map((situation, index) => (
                                <li key={index} style={{ marginBottom: '0.5rem' }}>
                                    {situation}
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="struggles" style={{
                        backgroundColor: '#f5f5f5',
                        padding: '1.5rem',
                        borderRadius: '8px',
                        borderLeft: '4px solid #f44336'
                    }}>
                        <div className="icon">👎</div>
                        <h4 style={{ 
                            marginBottom: '1rem',
                            fontSize: isMobile ? '1rem' : '1.2rem' 
                        }}>
                            WHERE THE {data.personality_type} STRUGGLES:
                        </h4>
                        <ul style={{ 
                            paddingLeft: '1.5rem',
                            margin: 0 
                        }}>
                            {typeInfo.struggles.map((struggle, index) => (
                                <li key={index} style={{ marginBottom: '0.5rem' }}>
                                    {struggle}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    const renderLearningStyles = () => {
        if (!data) return null;

        const chartData = {
            labels: ['Visual', 'Auditory', 'Kinesthetic'],
            datasets: [{
                data: [data.visual_score, data.auditory_score, data.kinesthetic_score],
                backgroundColor: ['#2196F3', '#FF9800', '#4CAF50'],
                borderWidth: 0
            }]
        };

        return (
            <div className="learning-styles-section" style={{
                padding: isMobile ? '1rem' : '2rem'
            }}>
                <h2 style={{ fontSize: isMobile ? '1.3rem' : '1.8rem' }}>
                    Your Learning Style Distribution
                </h2>
                
                <div className="pie-chart-container" style={{
                    maxWidth: isMobile ? '300px' : '400px',
                    margin: '0 auto'
                }}>
                    <Pie 
                        data={chartData}
                        options={{
                            ...chartData.options,
                            plugins: {
                                legend: {
                                    position: isMobile ? 'bottom' : 'right',
                                    labels: {
                                        boxWidth: isMobile ? 10 : 15,
                                        padding: isMobile ? 10 : 20,
                                        font: {
                                            size: isMobile ? 12 : 14
                                        }
                                    }
                                }
                            }
                        }}
                    />
                </div>
                
                <div className="learning-styles-details" style={{
                    gap: isMobile ? '1rem' : '2rem',
                    marginTop: isMobile ? '1rem' : '2rem'
                }}>
                    {['kinesthetic', 'auditory', 'visual'].map((style, index) => (
                        <div 
                            key={style}
                            className={`style-detail ${style} ${expandedStyle === style ? 'expanded' : ''}`}
                            onClick={() => setExpandedStyle(expandedStyle === style ? null : style)}
                        >
                            <div className="style-header">
                                <h3>{index + 1} - {style.charAt(0).toUpperCase() + style.slice(1)}</h3>
                                <span className="expand-icon">{expandedStyle === style ? '−' : '+'}</span>
                            </div>
                            {expandedStyle === style && (
                                <div className="style-content">
                                    <p className="style-description">
                                        {learningStyleContent[style].description}
                                    </p>
                                    <div className="learner-types">
                                        {learningStyleContent[style].learner_types.map((type, i) => (
                                            <div key={i} className="learner-type">
                                                <h4>{type.type}</h4>
                                                <p>{type.description}</p>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="style-tips">
                                        <h4>Tips for optimizing your learning:</h4>
                                        <ul>
                                            {learningStyleContent[style].tips.map((tip, i) => (
                                                <li key={i}>{tip}</li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (loading) return <div>Loading...</div>;
    if (error) return <div>Error: {error}</div>;
    if (!data) return <div>No results found</div>;

    return (
        <div className="quiz-results-page" style={{
            padding: isMobile ? '1rem' : '2rem',
            maxWidth: '100%',
            overflowX: 'hidden'
        }}>
            {renderHeader()}
            <div className="results-content" style={{
                gap: isMobile ? '1.5rem' : '3rem'
            }}>
                {renderDetailedBreakdown()}
                {renderPersonalityDetails()}
                {renderLearningStyles()}
            </div>
        </div>
    );
};

export default QuizResultsPage; 